/**
 * Unsaved-work drafts for IDM / SWP sessions that have no share code.
 *
 * An IDM/SWP activity opened without a share code has nowhere to put the user's
 * work: every save path in the app writes against a share code, so until the
 * user deliberately saves, a refresh (or a closed tab) loses everything they
 * placed on the canvas. This module keeps a draft of that work in
 * `localStorage`, restores it on the next load of the same activity, and drops
 * it the moment a real save succeeds.
 *
 * ## Why the draft is a `SaveStateJson`
 *
 * The draft is the *same* payload `extractUserSaveState` produces for a share-code
 * save, which buys the whole round-trip for free: `extractUserSaveState` reverses
 * the merge-time centering offset (`unShiftPoint`) and `mergePageData` re-applies
 * it, so a draft written from live state and merged back on load lands the
 * manipulatives exactly where they were. Rolling a bespoke draft format would
 * mean re-deriving that transform — and every widget's save shape — by hand.
 *
 * ## "Only changes": a sparse workspaces array
 *
 * Storing the full activity would waste space on pages the user never touched
 * (an IDM activity can carry ten authored pages of widgets). `mergePageData`
 * already resolves a page as `user.save_state.workspaces[i] ?? production[i]`,
 * i.e. per index, whole-workspace, with a nullish entry falling through to the
 * baseline. So a draft that carries a workspace ONLY for the pages that differ,
 * and `null` for the rest, is both minimal and needs no new merge path.
 *
 * "Differ" is measured against a snapshot of the activity as it was LOADED
 * (see `snapshotWorkspaces`), not against the production baseline directly:
 * `extractUserSaveState` normalises widget shapes, so an untouched page's
 * extract is never byte-identical to the raw baseline workspace it came from and
 * every page would look changed.
 *
 * Every `localStorage` access is wrapped: Safari private mode throws on access,
 * and a write can exceed the quota. A draft is a convenience, never a
 * correctness requirement, so failures are swallowed (with a console warning for
 * the quota case) rather than surfaced.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var KEY_PREFIX = 'mlc:unsavedDraft:';
var keyFor = function (activityKey) { return "".concat(KEY_PREFIX).concat(activityKey); };
/**
 * Identity of the activity a draft belongs to. The IDM revision id is the same
 * handle `loadActivityData` builds its data source from, so it is the natural
 * key; `location.pathname` covers the dev / file-based path where no mapping is
 * injected. Deliberately NOT scoped by user: drafts follow the activity.
 */
export function draftActivityKey() {
    var _a;
    if (typeof window === 'undefined')
        return null;
    var revisionId = (_a = window.__IDM_MAPPING__) === null || _a === void 0 ? void 0 : _a.revision_id;
    if (revisionId)
        return String(revisionId);
    return window.location.pathname || null;
}
/**
 * Once set, no further draft is written for the rest of the page's life.
 *
 * Two situations need this, and both are cases where a draft would actively
 * undo what the user just asked for:
 *
 *  - **Reset → "Reload this Activity"** and **"Start from Scratch"** navigate
 *    with `window.location.reload()` / `.replace('/')`. That fires `pagehide`,
 *    which flushes the draft — so the work the user just discarded would be
 *    written on the way out and restored on the way back in, making Reset look
 *    like it did nothing at all.
 *  - **After a successful save** a debounce timer armed moments earlier can
 *    still fire and re-create the draft that was just cleared. The React effect
 *    cleanup normally cancels it, but that depends on a re-render landing
 *    inside the save's async chain — too thin a thread to hang the guarantee on.
 *
 * Module-level rather than component state on purpose: the flag has to be
 * readable from a `pagehide` handler and from an already-armed timer's closure,
 * neither of which sees fresh React state.
 */
var draftsSuppressed = false;
/** Stop writing drafts for the rest of this page's life. Not reversible. */
export function suppressDrafts() {
    draftsSuppressed = true;
}
export function areDraftsSuppressed() {
    return draftsSuppressed;
}
/** Test-only: undo `suppressDrafts` so specs do not leak state into each other. */
export function resetDraftSuppressionForTests() {
    draftsSuppressed = false;
}
/**
 * Freeze the loaded state as the comparison reference. Called once per load,
 * with the extract of the pageData the activity opened with — including any
 * draft just restored into it, which makes a re-write of an untouched restored
 * draft a no-op rather than a duplicate.
 */
export function snapshotWorkspaces(state) {
    var _a, _b;
    return {
        workspaces: ((_b = (_a = state.save_state) === null || _a === void 0 ? void 0 : _a.workspaces) !== null && _b !== void 0 ? _b : []).map(function (ws) { return (ws ? stableStringify(ws) : null); }),
        appState: appStateFingerprint(state),
    };
}
/**
 * `appState` minus `saveTime` and `language`. `saveTime` is stamped with
 * `Date.now()` on every call and would otherwise report a change on every
 * single flush. `language` is excluded for the same reason cited in
 * `sparsifyDraft`'s `touched` param doc: a language switch re-merges every
 * page and must not by itself manufacture a draft — the whole point of
 * "drafts survive a language switch" is that toggling the language alone,
 * with no content edit, produces no draft. What remains —
 * `manipulativeBtnState`, `playerBgPattern` — is real user-facing state that
 * lives outside any page, so it has to take part in the change detection:
 * without it, toggling the background pattern with no widget edit produces
 * no draft and the change is lost on reload.
 */
function appStateFingerprint(state) {
    var _a, _b;
    var _c = ((_b = (_a = state.save_state) === null || _a === void 0 ? void 0 : _a.appState) !== null && _b !== void 0 ? _b : {}), saveTime = _c.saveTime, language = _c.language, rest = __rest(_c, ["saveTime", "language"]);
    void saveTime;
    void language;
    return stableStringify(rest);
}
/**
 * Null out every workspace that still matches the loaded snapshot. Returns
 * `null` when nothing differs, which the caller treats as "delete the draft" —
 * undoing an edit back to the loaded state should leave no draft behind, not a
 * draft that restores the same thing.
 */
export function sparsifyDraft(current, baseline, 
/**
 * Indices already known to hold user work, MUTATED to add any this call
 * finds. Stickiness is what makes the draft survive a language switch: that
 * re-merge rebuilds every page with localised card/dice/counter art, so a
 * fresh comparison would either report all ten pages as edited (bloating the
 * draft with pages the user never touched) or — after the baseline is
 * refreshed to match — report the genuinely edited pages as clean and drop
 * them. Remembering which pages the user actually touched decouples the two.
 */
touched) {
    var _a, _b;
    var workspaces = (_b = (_a = current.save_state) === null || _a === void 0 ? void 0 : _a.workspaces) !== null && _b !== void 0 ? _b : [];
    var sparse = workspaces.map(function (ws, index) {
        if (!ws)
            return null;
        if (stableStringify(ws) !== baseline.workspaces[index])
            touched.add(index);
        return touched.has(index) ? ws : null;
    });
    var appStateChanged = appStateFingerprint(current) !== baseline.appState;
    if (touched.size === 0 && !appStateChanged)
        return null;
    // Trailing nulls carry no information — the merge falls back to the baseline
    // for any index past the end of the array just as it does for a null entry.
    while (sparse.length > 0 && sparse[sparse.length - 1] === null)
        sparse.pop();
    return __assign(__assign({}, current), { save_state: __assign(__assign({}, current.save_state), { workspaces: sparse }) });
}
/** Read the draft for an activity, or `null` when there is none / it is unusable. */
export function readDraft(activityKey) {
    var _a;
    try {
        var raw = window.localStorage.getItem(keyFor(activityKey));
        if (!raw)
            return null;
        var parsed = JSON.parse(raw);
        // Shape guard: a hand-edited or half-written entry must not take down the
        // load. `workspaces` is the only field the merge actually requires.
        if (!parsed || !Array.isArray((_a = parsed === null || parsed === void 0 ? void 0 : parsed.save_state) === null || _a === void 0 ? void 0 : _a.workspaces))
            return null;
        return parsed;
    }
    catch (_b) {
        return null;
    }
}
/**
 * Returns whether the draft was actually stored.
 *
 * The caller needs to know: GameController only clears its dirty flag on a
 * successful write, so a quota failure is retried on the next watchdog tick
 * rather than being dropped until the user's next edit. A whole-activity
 * payload is 9-25KB and a classroom panel shares its origin quota, so
 * QuotaExceededError is a real outcome, not a theoretical one.
 */
export function writeDraft(activityKey, draft) {
    try {
        window.localStorage.setItem(keyFor(activityKey), JSON.stringify(draft));
        return true;
    }
    catch (err) {
        // Quota exceeded, or storage unavailable. Nothing to fall back to — the
        // draft is a convenience — but say so once, because the symptom otherwise
        // is silently losing unsaved work on reload.
        console.warn('[unsavedDraft] could not store draft', err);
        return false;
    }
}
export function clearDraft(activityKey) {
    try {
        window.localStorage.removeItem(keyFor(activityKey));
    }
    catch (_a) {
        // localStorage unavailable; nothing was stored either.
    }
}
/**
 * Clear the draft for the activity currently open, and stop writing new ones.
 *
 * Both halves matter. The clear is the point of the call; the suppression stops
 * an already-armed debounce timer, or a `pagehide` flush triggered by the
 * navigation that follows, from immediately writing the draft back — see
 * `suppressDrafts`.
 */
export function clearDraftForCurrentActivity() {
    suppressDrafts();
    var activityKey = draftActivityKey();
    if (activityKey)
        clearDraft(activityKey);
}
/**
 * Layer a draft on top of whatever user state the data source returned.
 *
 * Per page the draft wins when it has an entry — it is strictly newer than the
 * server's `/user-json`, being work done after that state was loaded — and the
 * server's entry stands where the draft has `null`. The result is a plain
 * `SaveStateJson`: `mergePageData` treats a missing index and a null entry
 * identically, so the sparseness survives without any special-casing.
 */
export function applyDraftToUserState(user, draft) {
    var _a, _b, _c, _d, _e, _f, _g;
    var draftWorkspaces = (_b = (_a = draft.save_state) === null || _a === void 0 ? void 0 : _a.workspaces) !== null && _b !== void 0 ? _b : [];
    var userWorkspaces = (_d = (_c = user === null || user === void 0 ? void 0 : user.save_state) === null || _c === void 0 ? void 0 : _c.workspaces) !== null && _d !== void 0 ? _d : [];
    var length = Math.max(draftWorkspaces.length, userWorkspaces.length);
    var workspaces = [];
    for (var index = 0; index < length; index++) {
        var merged = (_e = draftWorkspaces[index]) !== null && _e !== void 0 ? _e : userWorkspaces[index];
        // Holes are meaningful (fall back to production), so write the slot rather
        // than skipping it — `push`ing only the present ones would shift every
        // later page onto the wrong index.
        workspaces[index] = merged;
    }
    var base = user !== null && user !== void 0 ? user : draft;
    return __assign(__assign({}, base), { save_state: __assign(__assign({}, base.save_state), { 
            // appState (manipulativeBtnState / playerBgPattern) is whole-activity, not
            // per page. Merged FIELD-WISE rather than replaced wholesale: the draft
            // always carries an appState — `extractUserSaveState` emits one
            // unconditionally — and its `manipulativeBtnState` is `null` whenever the
            // session never had one, so an outright replacement would discard a real
            // value the server's `/user-json` had supplied.
            appState: mergeAppState((_f = user === null || user === void 0 ? void 0 : user.save_state) === null || _f === void 0 ? void 0 : _f.appState, (_g = draft.save_state) === null || _g === void 0 ? void 0 : _g.appState), workspaces: workspaces }) });
}
/** Field-wise appState merge: a draft field wins only when it has a value. */
function mergeAppState(serverAppState, draftAppState) {
    var merged = __assign({}, (serverAppState !== null && serverAppState !== void 0 ? serverAppState : {}));
    for (var _i = 0, _a = Object.entries(draftAppState !== null && draftAppState !== void 0 ? draftAppState : {}); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], value = _b[1];
        if (value != null)
            merged[key] = value;
    }
    return merged;
}
/**
 * Remove `dimensions` from any workspace whose production baseline had none.
 *
 * `extractUserSaveState` always writes `dimensions: {coordWidth, coordHeight}`,
 * and `coordWidth` falls back to the CURRENT canvas width when the activity's
 * production workspace carries no dimensions of its own. Persisting that pins
 * the page's coordinate box to whatever the canvas happened to be, and because
 * `mergePageData` derives `diffX = (playerWidth - jsonPageWidth) / 2` from it,
 * reloading at a different window size shifts everything by half the delta.
 * Leaving the key absent lets the reload re-derive it, which is what an
 * unsaved-work draft wants: a draft is reloaded constantly and at whatever size
 * the window happens to be, unlike a deliberate save.
 */
export function dropSyntheticDimensions(state, productionBaseline) {
    var _a, _b, _c, _d;
    var prodWorkspaces = (_b = (_a = productionBaseline === null || productionBaseline === void 0 ? void 0 : productionBaseline.save_state) === null || _a === void 0 ? void 0 : _a.workspaces) !== null && _b !== void 0 ? _b : [];
    return __assign(__assign({}, state), { save_state: __assign(__assign({}, state.save_state), { workspaces: ((_d = (_c = state.save_state) === null || _c === void 0 ? void 0 : _c.workspaces) !== null && _d !== void 0 ? _d : []).map(function (ws, index) {
                var _a;
                if (!(ws === null || ws === void 0 ? void 0 : ws.dimensions))
                    return ws;
                if ((_a = prodWorkspaces[index]) === null || _a === void 0 ? void 0 : _a.dimensions)
                    return ws;
                var dimensions = ws.dimensions, rest = __rest(ws, ["dimensions"]);
                void dimensions;
                return rest;
            }) }) });
}
/**
 * Overlay the live (DataContext) state of the current page onto the store's
 * `pageData`, producing the array `extractUserSaveState` expects.
 *
 * The save paths (`SaveWorkModal`, `InputWithBtn`) each build this the same way:
 * `pageData` holds committed state for every page, but the page on screen is
 * only committed on a page change, so without the overlay a draft would lag one
 * page-change behind the user's edits.
 */
export function mergeLivePageData(pageData, currentPage, live) {
    var index = Math.max(0, currentPage - 1);
    if (index >= pageData.length) {
        var padded = __spreadArray([], pageData, true);
        while (padded.length <= index) {
            padded.push({
                widgets: [],
                text: [],
                table: [],
                annotations: [],
                shades: [],
                drawCommands: [],
                coinsAndCountersDrawer: [],
            });
        }
        padded[index] = __assign(__assign({}, padded[index]), live);
        return padded;
    }
    return pageData.map(function (page, i) { return (i === index ? __assign(__assign({}, page), live) : page); });
}
/**
 * `JSON.stringify` with object keys sorted, so two structurally equal
 * workspaces compare equal regardless of the order their keys were built in.
 * Key order genuinely varies here: `extractUserSaveState` appends optional keys
 * (`hideTrayCounter`, `userZoom`, `openPanelSelection`, `dimensions`)
 * conditionally, and the widget mergers spread objects in different orders on
 * the load path than the save path, so a plain stringify reports spurious
 * changes and every page would be stored.
 */
function stableStringify(value) {
    return JSON.stringify(value, function (_key, val) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            return Object.keys(val)
                .sort()
                .reduce(function (acc, k) {
                acc[k] = val[k];
                return acc;
            }, {});
        }
        return val;
    });
}
